import { FC, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../../../utils/cn';
import { statusStyles, type TherapistAppointment } from './types';
import { formatDate, formatTime, getDaysInMonth, isToday, isCurrentMonth } from './dateUtils';

interface MonthViewProps {
  date: Date;
  appointments: TherapistAppointment[];
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const MonthView: FC<MonthViewProps> = ({ date, appointments }) => {
  const days = getDaysInMonth(date);

  const appointmentsByDay = useMemo(() => {
    const byDay: Record<string, TherapistAppointment[]> = {};
    days.forEach((day) => {
      const dateStr = formatDate(day);
      byDay[dateStr] = appointments
        .filter((apt) => apt.date === dateStr)
        .sort((a, b) => a.startTime.localeCompare(b.startTime));
    });
    return byDay;
  }, [days, appointments]);

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
      {/* Header */}
      <div className="grid grid-cols-7 bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700">
        {DAY_NAMES.map((name) => (
          <div
            key={name}
            className="p-3 text-center text-sm font-semibold text-gray-700 dark:text-slate-300"
          >
            <span className="hidden sm:inline">{name}</span>
            <span className="sm:hidden">{name.charAt(0)}</span>
          </div>
        ))}
      </div>
      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const dateStr = formatDate(day);
          const dayAppointments = appointmentsByDay[dateStr] || [];
          const isInCurrentMonth = isCurrentMonth(day, date);

          return (
            <div
              key={dateStr}
              className={cn(
                'min-h-[100px] sm:min-h-[120px] p-1 sm:p-2 border-b border-r border-gray-200 dark:border-slate-700',
                !isInCurrentMonth && 'bg-gray-50 dark:bg-slate-900/50'
              )}
            >
              <div
                className={cn(
                  'text-sm font-medium mb-1 w-7 h-7 flex items-center justify-center rounded-full',
                  isToday(day)
                    ? 'bg-blue-600 text-white'
                    : isInCurrentMonth
                      ? 'text-gray-900 dark:text-slate-100'
                      : 'text-gray-400 dark:text-slate-500'
                )}
              >
                {day.getDate()}
              </div>
              <div className="space-y-1">
                {dayAppointments.slice(0, 2).map((apt) => {
                  const style = statusStyles[apt.status];
                  return (
                    <Link
                      key={apt.id}
                      to={`/therapist/appointments/${apt.id}`}
                      className={cn(
                        'block p-1 rounded text-xs truncate border-l-2',
                        style.bg,
                        style.text,
                        style.darkBg,
                        style.darkText,
                        style.border
                      )}
                    >
                      <span className="hidden sm:inline">
                        {formatTime(apt.startTime)} - {apt.clientName}
                      </span>
                      <span className="sm:hidden">{formatTime(apt.startTime)}</span>
                    </Link>
                  );
                })}
                {dayAppointments.length > 2 && (
                  <div className="text-xs text-gray-500 dark:text-slate-400 pl-1">
                    +{dayAppointments.length - 2} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
